import React, { useMemo, useState, useEffect } from 'react';
import {
  type DragStartEvent,
  type DragOverEvent,
  type DragEndEvent,
  type UniqueIdentifier,
  closestCorners,
  defaultDropAnimation,
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToWindowEdges,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import { arrayMove } from '@dnd-kit/sortable';
import { createPortal } from 'react-dom';
import get from 'lodash/get';

import { useInternalEventContext } from '../../../providers/InternalEventProvider';
import { useFormContext } from '../Form/useFormContext';

import type {
  SortableMoveItemBetweenContainersEventType,
  SortableMoveItemWithinContainerEventType,
} from './sortable.types';
import { RawSortableItem } from './RawSortableItem';
import { SortableContext } from './SortableContext';

export type SortableProps<T = Record<string, unknown>> = {
  name?: string;
  children: React.ReactNode;
  options: T[];
  displayKey: string;
  valueKey: string;
  shouldHighlight?: (data: T) => boolean;
  onItemClicked?: (data: T) => void;
};

type SortableFormValues = Record<string, UniqueIdentifier[]>;

export const Sortable = <
  T extends Record<string, unknown> = Record<string, unknown>,
>({
  name = 'SORTABLE',
  children,
  options,
  displayKey,
  valueKey,
  shouldHighlight,
  onItemClicked,
}: SortableProps<T>) => {
  const internalEventContext = useInternalEventContext();

  const { values, setValues, setFieldTouched } =
    useFormContext<SortableFormValues>();

  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [activeHideHandle, setActiveHideHandle] = useState<boolean>(false);

  const moveWithinContainer = async ({
    dragItemId,
    dropItemId,
    sourceContainer,
  }: SortableMoveItemWithinContainerEventType) => {
    const containerItems = values?.[sourceContainer] || [];
    const oldIndex = containerItems.indexOf(dragItemId);
    const newIndex = containerItems.indexOf(dropItemId);

    if (oldIndex !== newIndex) {
      const reorderedItems = arrayMove(containerItems, oldIndex, newIndex);

      await setValues({
        ...values,
        [sourceContainer]: reorderedItems,
      });
      // NOTE: Need to manually set touched fields here to handle validation
      await setFieldTouched(sourceContainer);
    }
  };

  const moveBetweenContainers = async ({
    dragItemId,
    dropItemId,
    sourceContainer,
    targetContainer,
    overType,
  }: SortableMoveItemBetweenContainersEventType) => {
    const sourceItems = values?.[sourceContainer] || [];
    const destItems = values?.[targetContainer] || [];
    const overIndex = destItems.indexOf(dropItemId);

    let newIndex;
    if (overType === 'container') {
      // Dropping over container itself
      newIndex = destItems.length;
    } else {
      // Dropping over an item
      newIndex = overIndex === -1 ? destItems.length : overIndex;
    }

    // Remove from source
    const newSourceItems = sourceItems.filter(item => item !== dragItemId);

    // Add to destination
    const newDestItems = [
      ...destItems.slice(0, newIndex),
      dragItemId,
      ...destItems.slice(newIndex),
    ];

    await setValues({
      ...values,
      [sourceContainer]: newSourceItems,
      [targetContainer]: newDestItems,
    });
    // NOTE: Need to manually set touched fields here to handle validation
    await setFieldTouched(sourceContainer);
    await setFieldTouched(targetContainer);
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id);
    setActiveHideHandle(event.active.data.current?.hideHandle ?? false);
  };

  const handleDragOverAsync = async (event: DragOverEvent) => {
    const { active, over } = event;

    if (!over) return;

    const activeContainer = active.data.current?.containerId;
    const overContainer = over.data.current?.containerId;

    if (!activeContainer || !overContainer) return;

    // Moving between different fields
    if (activeContainer !== overContainer) {
      await moveBetweenContainers({
        dragItemId: active.id,
        dropItemId: over.id,
        sourceContainer: activeContainer,
        targetContainer: overContainer,
        overType: over.data.current?.type,
      });
    }
  };

  const handleDragEndAsync = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) return; // Dropped outside any droppable area

    const activeContainer = active.data.current?.containerId;
    const overContainer = over.data.current?.containerId;

    if (!activeContainer || !overContainer) return;

    return activeContainer === overContainer
      ? moveWithinContainer({
          dragItemId: active.id,
          dropItemId: over.id,
          sourceContainer: activeContainer,
        })
      : moveBetweenContainers({
          dragItemId: active.id,
          dropItemId: over.id,
          sourceContainer: activeContainer,
          targetContainer: overContainer,
          overType: over.data.current?.type,
        });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    void handleDragEndAsync(event);
    setActiveId(null);
    setActiveHideHandle(false);
  };

  const handleDragOver = (event: DragOverEvent) => {
    void handleDragOverAsync(event);
  };

  const sortableProviderValue = useMemo(
    () => ({
      displayByValueLookup: options.reduce((acc, curr) => {
        const display = get(curr, displayKey) as React.ReactNode;
        const v = get(curr, valueKey) as string;
        return {
          ...acc,
          ...(display && v ? { [v]: display } : {}),
        };
      }, {}) as Record<string, React.ReactNode>,
      dataByValueLookup: options.reduce((acc, curr) => {
        const v = get(curr, valueKey) as string;
        return {
          ...acc,
          ...(v ? { [v]: curr } : {}),
        };
      }, {}),
      shouldHighlight: shouldHighlight as
        ((data: Record<string, unknown>) => boolean) | undefined,
      onItemClicked: onItemClicked as
        ((data: Record<string, unknown>) => void) | undefined,
    }),
    [options, displayKey, valueKey, shouldHighlight, onItemClicked]
  );

  useEffect(() => {
    const moveItemWithinContainerEvent =
      internalEventContext.sortableMoveItemWithinContainerEvent.subscribe(v => {
        void moveWithinContainer(v);
      });

    const moveItemBetweenContainerEvent =
      internalEventContext.sortableMoveItemBetweenContainersEvent.subscribe(
        v => {
          void moveBetweenContainers(v);
        }
      );

    return () => {
      moveItemWithinContainerEvent.unsubscribe();
      moveItemBetweenContainerEvent.unsubscribe();
    };
  }, []);

  return (
    <div data-test={name}>
      <DndContext
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        sensors={sensors}
        modifiers={[restrictToWindowEdges, restrictToVerticalAxis]}
      >
        <SortableContext.Provider value={sortableProviderValue}>
          {children}
        </SortableContext.Provider>
        {createPortal(
          <DragOverlay
            adjustScale={false}
            dropAnimation={defaultDropAnimation}
            className="sortable-drag-overlay-portal"
          >
            {activeId ? (
              <div className="sortable-drag-overlay">
                <RawSortableItem
                  key={activeId}
                  value={activeId}
                  dismissible={false}
                  hideHandle={activeHideHandle}
                  name="sortable-overlay"
                >
                  {sortableProviderValue.displayByValueLookup[activeId]}
                </RawSortableItem>
              </div>
            ) : null}
          </DragOverlay>,
          document.body
        )}
      </DndContext>
    </div>
  );
};
